import {AsyncPipe, DatePipe, DecimalPipe, NgTemplateOutlet} from '@angular/common'
import {ApplicationRef, Component, inject, OnInit} from '@angular/core'
import {FormControl, ReactiveFormsModule} from '@angular/forms'
import {MatButton, MatFabButton, MatIconButton} from '@angular/material/button'
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card'
import {MatCheckbox} from '@angular/material/checkbox'
import {MatOption} from '@angular/material/core'
import {MatDialog} from '@angular/material/dialog'
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatSelect} from '@angular/material/select'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table'
import {MatTab, MatTabGroup} from '@angular/material/tabs'
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router'
import {
  BlancoEventCode,
  BlancoLoanApplication,
  eventCodes,
  IBlancoResultCode,
  IKalpLoan
} from '@sparbanken-syd/blanco-backend'
import {ConfigService} from '@sparbanken-syd/sparbanken-syd-bankid'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {first, ReplaySubject} from 'rxjs'
import {filter} from 'rxjs/operators'
import {WINDOW} from '../../application/app'
import {AdminService} from '../../services/admin.service'
import {DelegateComponent} from '../delegate/delegate.component'
import {EVENT_CODE_TITLES, IndicatorComponent} from '../indicator/indicator.component'
import {UcreportComponent} from '../ucreport/ucreport.component'

@Component({
  selector: 'spb-blanco-details',
  templateUrl: './blanco-details.component.html',
  styleUrls: ['./blanco-details.component.scss'],
  imports: [
    MatFabButton,
    MatIcon,
    MatButton,
    RouterLink,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    IndicatorComponent,
    NgTemplateOutlet,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatTabGroup,
    MatTab,
    UcreportComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    MatIconButton
  ]
})
export class BlancoDetailsComponent implements OnInit {
  // Status code for closed
  public BLANCO_STATUS_CLOSED = 9

  // Translate codes to readable text
  public titles = EVENT_CODE_TITLES

  // Actions admin can add
  public actions: { title: string; value: BlancoEventCode }[] = [
    {title: 'Inkomstuppgifter kontrollerade', value: eventCodes.incomeReview},
    {title: 'Lån skapat i MP', value: eventCodes.createdMp},
    {title: 'Kund kontaktad', value: eventCodes.contact},
    {title: 'Kontaktförsök - Misslyckat', value: eventCodes.contactFail},
    {title: 'Lån accepterat av Kund', value: eventCodes.accepted},
    {title: 'Ta bort ansökan', value: eventCodes.closed}
  ]

  public actionControl = new FormControl<BlancoEventCode | ''>('')

  public application$ = new ReplaySubject<BlancoLoanApplication>(1)

  // Translation lists
  public LIVINGS = ['Villa', 'Bostadsrätt', 'Hyresrätt', 'Fritidshus']
  public OCCUPATIONS = ['Tillsvidare', 'Pensionär', 'Visstid', 'Timanställd', 'Egenföretagare', 'Student', 'Arbetssökande']

  // Table setup
  public loanColumnsToShow = ['type', 'amount', 'interest', 'interestMonth', 'repaymentMonth', 'cost', 'solve']
  public loanDataSource = new MatTableDataSource<IKalpLoan>([])

  public currentItem = 0
  protected readonly eventCodes = eventCodes
  private application: BlancoLoanApplication | undefined

  private readonly injectedWindow = inject(WINDOW)
  private readonly route = inject(ActivatedRoute)
  private readonly adminService = inject(AdminService)
  private readonly configService = inject(ConfigService)
  private readonly dialog = inject(MatDialog)
  private readonly router = inject(Router)
  private readonly takeUntilDestroyed = getTakeUntilDestroyed()
  private readonly appRef = inject(ApplicationRef)

  public ngOnInit(): void {
    if (!this.configService.isAdmin()) {
      this.router.navigate(['/admin']).then()
    }

    this.adminService.currentApplication$
      .pipe(
        this.takeUntilDestroyed(),
        filter((app) => !!app.applicants)
      )
      .subscribe(app => {
        app.codes = app.codes.toSorted((a, b) => b.timeStamp - a.timeStamp)
        app.codes.forEach((code) => {
          code.title = this.titles[code.name]
        })
        this.loanDataSource = new MatTableDataSource(app.kalpResult?.loans)
        this.application = app
        this.application$.next(app)
      })

    this.route.paramMap.subscribe((params: ParamMap) => {
      this.adminService.getApplication(params.get('id'))
    })
  }

  /**
   * Pushes a new code to codes and saves it.
   */
  public doAction(): void {
    this.application?.codes.unshift({
      name: this.actionControl.value as BlancoEventCode,
      pass: true,
      state: 'ok',
      timeStamp: Date.now(),
      title: this.titles[this.actionControl.value as string],
      values: [this.configService.currentUser()?.sId]
    })

    if (this.actionControl.value === eventCodes.closed) {
      this.application!.blancoStatus = this.BLANCO_STATUS_CLOSED
    }
    this.adminService.saveApplication(this.application)
    this.actionControl.reset()
  }

  /**
   * Remove a CODE, implemented for income check only. Use timeStamp
   * to delete a specific code if several exist
   */
  public removeCode(code: IBlancoResultCode): void {
    this.application!.codes = this.application!.codes.filter(c => {
      if (c.name !== code.name) {
        return true
      }
      // If same name check if same timestamp
      return c.timeStamp !== code.timeStamp
    })
    this.adminService.saveApplication(this.application)
  }

  /**
   * Simply opens the assign dialog.
   */
  public delegate(): void {
    this.dialog.open(DelegateComponent, {
      data: {application: this.application},
      disableClose: true
    })
  }

  public isNumber(val: any): boolean {
    return typeof val === 'number'
  }

  public print(): void {
    this.appRef.isStable
      .pipe(filter(Boolean), first())
      .subscribe(() => {
        this.injectedWindow.print()
      })
  }
}